import isEqual from "lodash/isEqual";

import {CheckInfo} from "@/lib/queries/statuspages.ts";
import {MetricsExplorerSchema} from "@/lib/types/metricsExplorer.ts";
import {ExplorerConfig, ExplorerSeriesConfig} from "@/pages/metricsExplorer/explorerConfig.ts";
import {
  availableDataTypes,
  availableMetrics,
  findDataType,
  pruneSelections,
  uniqueCheckTypes,
} from "@/pages/metricsExplorer/schemaUtils.ts";

/** A field the catalog filled by choosing among several options, which the builder flags. */
export type GuessedField = "dataType" | "metrics";

interface CatalogDefaults {
  config: ExplorerConfig;
  /** Per series ref, for each series this pass changed: the fields it had to guess. */
  guessed: Record<string, GuessedField[]>;
}

/**
 * Fill in, and keep valid, whatever the catalog can decide for itself.
 *
 * A series that names checks gets a data type and metric its check types offer. That covers
 * a user who has just picked a check and should see a chart rather than more empty
 * dropdowns, a preset link that deliberately leaves these blank so the frontend never keeps
 * its own copy of which tables a check writes to, and a user who has switched checks to
 * another type, whose old data type the new one does not write. A choice that is still
 * valid is kept; one that is not is replaced by the only option, or else by the first, which
 * is also the server's default, and reported as guessed.
 *
 * Returns the original config unchanged when there is nothing to fill, so a caller can use
 * identity to decide whether to write back.
 */
export const applyCatalogDefaults = (
  config: ExplorerConfig,
  schema: MetricsExplorerSchema | undefined,
  checkMeta: Record<number, CheckInfo>
): CatalogDefaults => {
  if (!schema) {
    return {config, guessed: {}};
  }
  const guessed: Record<string, GuessedField[]> = {};
  const series = config.series.map((entry) => {
    const filled = fillSeries(entry, schema, checkMeta);
    if (filled.series !== entry) {
      guessed[entry.ref] = filled.guessed;
    }
    return filled.series;
  });
  return Object.keys(guessed).length > 0 ? {config: {...config, series}, guessed} : {config, guessed};
};

const fillSeries = (
  series: ExplorerSeriesConfig,
  schema: MetricsExplorerSchema,
  checkMeta: Record<number, CheckInfo>
): {series: ExplorerSeriesConfig; guessed: GuessedField[]} => {
  const unchanged = {series, guessed: []};
  // Until every check's type is known, a data type valid for all of them may look invalid
  // for the ones resolved so far; replacing it then would throw away a good choice.
  if (series.checkIds.length === 0 || !series.checkIds.every((id) => checkMeta[id]?.monitoring_service_type)) {
    return unchanged;
  }
  const checkTypes = uniqueCheckTypes(series.checkIds.map((id) => checkMeta[id].monitoring_service_type!));
  const dataTypes = availableDataTypes(schema, checkTypes);
  // Check types with no table in common: nothing to pick, and the builder says so.
  if (dataTypes.length === 0) {
    return unchanged;
  }
  const keepsDataType = dataTypes.some((dataType) => dataType.name === series.dataType);
  const dataType = keepsDataType ? findDataType(schema, checkTypes, series.dataType)! : dataTypes[0];
  const pruned = pruneSelections(dataType, series.metrics, series.groupBy, series.filters);
  const metricOptions = availableMetrics(dataType);
  const keepsMetrics = pruned.metrics.length > 0;
  const metrics = keepsMetrics || metricOptions.length === 0 ? pruned.metrics : [metricOptions[0].name];

  const next = {...series, dataType: dataType.name, ...pruned, metrics};
  // Compare on content, not identity: pruning hands back fresh arrays every pass, and the
  // caller would otherwise write forever.
  if (isEqual(next, series)) {
    return unchanged;
  }
  const guessed: GuessedField[] = [];
  if (!keepsDataType && dataTypes.length > 1) {
    guessed.push("dataType");
  }
  if (!keepsMetrics && metricOptions.length > 1) {
    guessed.push("metrics");
  }
  return {series: next, guessed};
};
